import { supabase } from "../supabaseClient";

/**
 * Fetch ALL aop_plans rows (any status) so the dashboard can build the FY dropdown.
 * Returns: { plans: [...], error }
 */
export const fetchAllAOPPlans = async () => {
  try {
    const { data, error } = await supabase
      .from("aop_plans")
      .select(
        "id, financial_year, status, validity_start_date, validity_end_date, priority"
      )
      .order("priority", { ascending: false });

    if (error) throw error;
    return { plans: data || [], error: null };
  } catch (err) {
    console.error("❌ fetchAllAOPPlans error:", err);
    return { plans: [], error: err };
  }
};

/**
 * Fetch bookings + slabs for a specific AOP plan.
 * All other logic unchanged from original.
 */
export const fetchAOPData = async (cpId, planId = null) => {
  if (!cpId) return { data: [], slabs: [], error: null };

  try {
    let resolvedPlanId = planId;

    // If no planId supplied, fall back to highest-priority Active plan (original behaviour)
    if (!resolvedPlanId) {
      const { data: plan, error: planError } = await supabase
        .from("aop_plans")
        .select("id")
        .eq("status", "Active")
        .order("priority", { ascending: false })
        .limit(1);

      if (planError) throw planError;
      resolvedPlanId = plan?.[0]?.id;
    }

    if (!resolvedPlanId) {
      console.warn("⚠️ No AOP plan found");
      return { data: [], slabs: [], error: null };
    }

    // Fetch CP booking data
    const { data, error } = await supabase
      .from("CpBookingComputState")
      .select(`
        allotment_payment_id,
        booking_value,
        aop_amount,
        project_id,
        aop_plan_id,
        aop_slab_id,
        created_at,

        aop_plans (
          id,
          financial_year
        )
      `)
      .eq("cp_id", cpId)
      .eq("aop_plan_id", resolvedPlanId);

    if (error) throw error;

    const uniqueProjectIds = [...new Set((data || []).map((r) => r.project_id).filter(Boolean))];
    let projectNameMap = {};
    if (uniqueProjectIds.length > 0) {
      const { data: projects } = await supabase
        .from("Projects")
        .select("projectId, projectName, projectShortCode")
        .in("projectId", uniqueProjectIds);
      (projects || []).forEach((p) => {
        projectNameMap[p.projectId] = { name: p.projectName, shortCode: p.projectShortCode };
      });
    }

    const { data: payouts, error: payoutError } = await supabase
      .from("CpProjectPayout")
      .select(`
    project_id,
    enablePayout,
    isCpPaymentEntryDone,
    aop_plan_id
  `)
      .eq("cp_id", cpId)
      .eq("aop_plan_id", resolvedPlanId)
      .eq("component_type", "AOP");

    if (payoutError) throw payoutError;
    const payoutMap = {};

    (payouts || []).forEach((p) => {
      payoutMap[p.project_id] = {
        enablePayout: p.enablePayout,
        isCpPaymentEntryDone: p.isCpPaymentEntryDone,
      };
    });

    const allotmentIds = (data || []).map((r) => r.allotment_payment_id).filter(Boolean);
    let agreementMap = {};
    if (allotmentIds.length > 0) {
      const { data: agreements } = await supabase
        .from("AgreementForm")
        .select(`allotmentPaymentId, agreementStatus`)
        .in("allotmentPaymentId", allotmentIds);
      (agreements || []).forEach((a) => {
        agreementMap[a.allotmentPaymentId] = a.agreementStatus?.agreement_date || null;
      });
    }

    const enrichedData = (data || []).map((row) => ({
      ...row,
      Projects: {
        projectId: row.project_id,
        projectName: projectNameMap[row.project_id]?.name || null,
        projectShortCode: projectNameMap[row.project_id]?.shortCode || null,
      },
      enablePayout: payoutMap[row.project_id]?.enablePayout || false,
      isCpPaymentEntryDone:
        payoutMap[row.project_id]?.isCpPaymentEntryDone || false,
      agreement_date: agreementMap[row.allotment_payment_id] || null,
    }));

    // Fetch ALL slabs of that plan
    const { data: allSlabs, error: slabError } = await supabase
      .from("aop_slabs")
      .select(`
        id,
        from_value,
        to_value,
        percentage,
        amount,
        sort_order
      `)
      .eq("aop_plan_id", resolvedPlanId)
      .order("sort_order", { ascending: true });

    if (slabError) throw slabError;
    return {
      data: enrichedData,   // ✅ use enriched data
      slabs: allSlabs || [],
      error: null,
    };
  } catch (err) {
    console.error("❌ fetchAOPData error:", err);
    return { data: [], slabs: [], error: err };
  }
};


export const fetchAllAOPPlansData = async (cpId) => {
  if (!cpId) return { plans: [] };

  try {
    // 1. Get all AOP plans
    const { plans } = await fetchAllAOPPlans();

    const formattedPlans = await Promise.all(
      (plans || []).map(async (plan) => {
        // 2. Fetch data for each plan
        const { data, slabs } = await fetchAOPData(cpId, plan.id);

        return {
          plan,
          data: data || [],
          slabs: slabs || [],
        };
      })
    );

    return { plans: formattedPlans };
  } catch (err) {
    console.error("❌ fetchAllAOPPlansData error:", err);
    return { plans: [] };
  }
};